import { Box, Paper, PinInput, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { LogoWithIcon } from 'src/UI/app-container/header/Header';
import { Button, Link } from 'src/components';
import { AuthFeedback } from 'src/auth/components/AuthFeedback';

const codeSchema = z.object({
  code: z.string().length(6, 'Code must be 6 digits'),
});

type CodeFormValues = z.infer<typeof codeSchema>;

interface RecoveryCodeFormProps {
  email: string;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: (code: string) => void;
  onBack: () => void;
}

export const RecoveryCodeForm = ({
  email,
  error,
  isSubmitting,
  onSubmit,
  onBack,
}: RecoveryCodeFormProps) => {
  const form = useForm<CodeFormValues>({
    mode: 'controlled',
    initialValues: {
      code: '',
    },
    validate: zodResolver(codeSchema),
    validateInputOnChange: true,
  });

  const handleSubmit = (values: CodeFormValues) => {
    onSubmit(values.code);
  };

  const isValidCode = form.values.code.length === 6;

  return (
    <Paper bg="gray.0" radius="xl" py={48} px={84} w={490}>
      <Stack gap="md">
        <Box mb="lg">
          <LogoWithIcon textColor="gray.10" />
        </Box>

        <Text fz="xl" fw={700} c="gray.10">
          Enter your code
        </Text>

        <Text fz="md" fw={500} c="gray.10" mb="xs">
          We&apos;ve sent a 6-digit code to{' '}
          <Text span fw={600}>
            {email}
          </Text>
        </Text>

        <AuthFeedback error={error} />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md" align="center">
            <PinInput
              length={6}
              type="number"
              size="lg"
              autoFocus
              key={form.key('code')}
              {...form.getInputProps('code')}
              oneTimeCode
              aria-label="Recovery code"
            />

            <Button
              type="submit"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting || !isValidCode}
              variant="primary"
            >
              Verify code
            </Button>
          </Stack>
        </form>

        <Text ta="center" size="sm" c="gray.6">
          Didn&apos;t receive the code? Check your spam folder or{' '}
          <Text
            component="button"
            type="button"
            onClick={onBack}
            c="orange"
            td="underline"
            style={{ cursor: 'pointer', background: 'none', border: 'none', font: 'inherit' }}
          >
            try again
          </Text>
        </Text>

        <Text ta="center" size="sm">
          <Link to="/login" c="orange" size="textSm">
            Back to login
          </Link>
        </Text>
      </Stack>
    </Paper>
  );
};
